import { FC, useEffect, useMemo } from 'react';
import { Field } from 'redux-form';

import ThemedMaskedInput from 'inquiry/components/ThemedMaskedInput';
import { parseEmptyStringToNull } from 'inquiry/lib/form';
import { useApply, useValidators } from 'inquiry/lib/jsonLogic';
import { useShouldShowServerError } from 'inquiry/lib/serverError';
import { InputMaskedTextComponent } from 'lib/interfaces/ui-components';
import isEmpty from 'lib/utils/formValidation/isEmpty';
import { UiComponentActionsApi } from 'verify/lib/interfaces/uiComponent';
import { Inquiry } from 'verify/models/inquiry';

interface InputMaskedTextProps {
  component: InputMaskedTextComponent;
  inquiry: Inquiry;
  inquiryLoading: boolean;
  actionsApi: UiComponentActionsApi;
}

const InputMaskedText: FC<InputMaskedTextProps> = ({
  component,
  inquiry,
  inquiryLoading,
  actionsApi,
}: InputMaskedTextProps) => {
  const {
    disabled,
    error,
    hidden,
    label,
    mask,
    optional,
    placeholder,
    prefill,
    secure,
    validations,
  } = component.attributes;

  const formField = component.name;

  const shouldShowServerError = useShouldShowServerError(
    inquiry,
    inquiryLoading,
    formField,
    prefill,
  );

  const parsedMask = useMemo(
    () =>
      mask != null
        ? mask.split('').map((char) => {
            switch (char) {
              case '#': // number
                return /\d/;
              case '@': // all letters
                return /[a-z]/i;
              case '*': // number or letter
                return /\w/;
              default:
                return char;
            }
          })
        : false,
    [mask],
  );

  const apply = useApply(formField, inquiry.attributes['next-step'].name);
  const validators = useValidators({ hidden, optional, validations });

  // TODO: figure out how to test this
  const isHidden = apply<boolean>(hidden);

  useEffect(() => {
    if (isHidden) {
      // Setting to '' removes the field value from form state entirely, unlike null which sets value to null.
      actionsApi.setField(formField, '');
    } else if (!isHidden && !isEmpty(prefill)) {
      actionsApi.setField(formField, prefill);
    }
  }, [isHidden]);

  if (isHidden) {
    return null;
  }

  return (
    <Field
      name={formField}
      id={formField}
      component={ThemedMaskedInput}
      disabled={apply<boolean>(disabled)}
      parse={parseEmptyStringToNull}
      validate={validators}
      label={label}
      secure={secure}
      mask={parsedMask}
      placeholder={placeholder}
      serverError={shouldShowServerError ? error : null}
      styles={component.styles}
    />
  );
};

export default InputMaskedText;
